import React from "react";
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import './index.css'
import '@strivelabs/ui-blocks/styles.css'; // Import TipTap editor styles
import { ClerkProvider } from '@clerk/clerk-react'
import { PostHogProvider } from 'posthog-js/react'
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import '@fontsource/inter/400.css'; // Regular weight
import '@fontsource/inter/500.css'; // Medium weight
import '@fontsource/inter/600.css'; // Semi-bold
import '@fontsource/inter/700.css'; // Bold
import '@fontsource/fraunces/400.css'; // Fraunces font
import { ErrorBoundary } from '@/components/common';
import { logger, logInfo } from '@/lib/logger';
import { getDomainConfig } from '@/config/domainConfig';
// @demo: added for demo purposes
import { Toaster } from '@strivelabs/ui-core';

const env = import.meta.env.VITE_APP_NODE_ENV;

const isProduction = env === "production";

const options = {
  api_host: import.meta.env.VITE_APP_PUBLIC_POSTHOG_HOST,
  disable_session_recording: !isProduction,
  disable_event_autotrack: !isProduction,
};

// Clerk publishable key is resolved from the hostname via domainConfig
const PUBLISHABLE_KEY = getDomainConfig().clerkPublishableKey;

// Enhanced QueryClient with global error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Check if error has a status code
        if (typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
          // Always retry 401 errors (up to 3 times) to allow Clerk token refresh
          // This handles cases where the user returns to a stale tab
          if (error.status === 401) {
            return failureCount < 3;
          }
          // Don't retry on other 4xx errors (client errors)
          if (error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Add retry delay for exponential backoff, helps with 401 retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      // Prevent automatic refetching when switching tabs
      // This is critical for chat sessions - we don't want to disrupt ongoing streams
      refetchOnWindowFocus: false,
      // Set stale time to prevent unnecessary refetches
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      onError: (error) => {
        logger(error instanceof Error ? error : new Error(String(error)), {
          context: 'React Query - Mutation Error',
          type: 'mutation_error',
        });
      },
    },
  },
});

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key")
}

// Log app initialization
logInfo('Application starting', { 
  mode: import.meta.env.MODE,
  important: true 
});

const DEFAULT_AFTER_SIGN_IN_URL = import.meta.env.VITE_CLERK_AFTER_SIGN_IN_URL || "/ui";
const DEFAULT_AFTER_SIGN_UP_URL = import.meta.env.VITE_CLERK_AFTER_SIGN_UP_URL || "/ui/onboarding";

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <PostHogProvider
        apiKey={import.meta.env.VITE_APP_PUBLIC_POSTHOG_KEY}
        options={options}
      >
        <ClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          afterSignOutUrl={`${window.location.origin}/`}
          afterSignInUrl={`${window.location.origin}${DEFAULT_AFTER_SIGN_IN_URL}`}
          afterSignUpUrl={`${window.location.origin}${DEFAULT_AFTER_SIGN_UP_URL}`}
        >
          <QueryClientProvider client={queryClient}>
            <App />
            <Toaster /> {/* @demo: added for demo purposes */}
          </QueryClientProvider>
        </ClerkProvider>
      </PostHogProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
